<script setup lang="ts">
import { computed, ref } from 'vue'
import { IExtendedDegree, IExtendedDegreePackage } from '@shared/types/extensions'
import FirmaComponent from '@/components/FirmaComponent.vue'
import { useStore } from '@/store'
import { ref as dbRef, get, query, orderByChild, equalTo } from 'firebase/database'
import { database } from '@/firebase/config'
import { degreeService } from '@/services/degreeService'
import { formatDate } from '@/utils/dateFormatter'
import BaseModal from '../BaseModal.vue'
const db = database

const store = useStore()
const props = defineProps<{
	modelValue: boolean
	package: IExtendedDegreePackage
	testMode: boolean
	institutionId: string
	cadenasAFirmar: string[]
	deviceId: string
}>()
// Firebase paths
const getInstitutionPath = (path: string) => `institutions/${props.institutionId}${props.testMode ? '/testMode' : ''}${path}`
const emit = defineEmits(['update:modelValue', 'degree-updated', 'refresh-packages'])
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
const userCurp = computed(() => store.state.global.user.me.curp || '')

// Variables para el servicio de firma
const face = ref('')
const signatureFace = ref('')

const certificado = computed(() => store.state.global.user.me.cer)
// Manejar firma completada
const firmaCompletada = ref(false)
const firmaData = ref<{
	firmas: string[]
	face: string
	signatureFace: string
} | null>(null)

const closeModalDetails = () => {
	showModal.value = false
}
// Manejar error de firma
const handleFirmaError = (error: string) => {
	console.error('Error al procesar las firmas:', error)
	store.dispatch('notification/showError', error)
}
const handleFirmaCompletada = async (data: { firmas: string[]; face: string; signatureFace: string }) => {
	firmaData.value = data
	firmaCompletada.value = true
	emit('refresh-packages')
}
/**
 * @description Computed property que obtiene los responsables del paquete
 * @type {ComputedRef<any>}
 */
const responsables = computed(() => props.package?.responsables)

// Enviar firmas
const enviarFirmas = async () => {
	if (!props.package || !firmaData.value) return

	try {
		const packageToSign = props.package

		// Obtener los títulos del paquete
		const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageToSign.id))
		const snapshot = await get(packageQuery)

		if (!snapshot.exists()) {
			throw new Error('No se encontraron títulos en el paquete')
		}

		const documents = snapshot.val() as Record<string, IExtendedDegree>
		const degreesToSign = []

		// Preparar los títulos para firmar
		for (const [id, degree] of Object.entries(documents)) {
			let indexByCadena = -1

			// Buscar la cadena correspondiente en props.cadenasAFirmar
			if (typeof degree.cadenaOriginal === 'object') {
				const foundCadena = degree.cadenaOriginal[userCurp.value]
				indexByCadena = props.cadenasAFirmar.findIndex(cadena => cadena === foundCadena)
			} else if (typeof degree.cadenaOriginal === 'string') {
				indexByCadena = props.cadenasAFirmar.findIndex(cadena => cadena === degree.cadenaOriginal)
			} else {
				console.warn('degree.cadenaOriginal para degree', id, ':', degree.cadenaOriginal)
				continue
			}

			if (indexByCadena >= 0 && indexByCadena < firmaData.value.firmas.length) {
				const resultado = firmaData.value.firmas[indexByCadena] as unknown as string
				degreesToSign.push({
					id,
					signature: resultado
				})
			} else {
				console.error('No se encontró firma correspondiente para degree', id, 'con indexByCadena:', indexByCadena)
			}
		}

		// Actualizar las variables de firma
		face.value = firmaData.value.face
		signatureFace.value = firmaData.value.signatureFace

		// Llamar al servicio addSignatures
		await degreeService.addSignatures(props.institutionId, face.value, signatureFace.value, props.deviceId, degreesToSign, props.testMode)

		// Limpiar el estado del modal
		showModal.value = false
		firmaCompletada.value = false
		firmaData.value = null

		// Emitir evento para refrescar paquetes con un pequeño delay para asegurar que Firebase se actualice
		setTimeout(() => {
			emit('refresh-packages')
		}, 1000)

		// Mostrar mensaje de éxito
		store.dispatch('notification/showSuccess', 'Firmas enviadas exitosamente')
	} catch (error: any) {
		console.error('Error al procesar las firmas:', error)
		store.dispatch('notification/showError', `Error: ${error.message}`)
	}
}
const getZip = async (id: number, name: string) => {
	try {
		await degreeService.getZipOfPackage({
			package: id,
			institution: props.institutionId,
			testMode: props.testMode,
			name: name
		})
	} catch (error: any) {
		showErrorModal.value = true
		errorMessage.value = error.message || 'Error al obtener el ZIP del paquete'
	}
}
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="props.package" class="ml20">
			<VaCardTitle :class="[props.testMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left" style="font-size: 22px; font-weight: bold; color: white">Detalles del título</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.testMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModalDetails" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>
			<VaCardContent class="mt-2">
				<div v-if="props.package.sign" class="alert alert-success mb-4">
					<i class="fas fa-check-circle me-2"></i>
					Los documentos de este paquete ya han sido firmados.
				</div>
				<div class="row" v-else>
					<div class="col-md-12 mb-4">
						<div class="signers-section">
							<h4 class="signers-title">
								<i class="fas fa-users me-2"></i>
								Responsables de firma
							</h4>
							<div class="signers-grid">
								<div v-for="(responsable, key) in responsables" :key="key" class="signer-card" :class="{ 'signed': responsable.sign, 'current-user': key === userCurp }">
									<div class="signer-header">
										<div class="signer-avatar">
											<i class="fas fa-user"></i>
										</div>
										<div class="signer-info">
											<h5 class="signer-name">{{ responsable.name }}</h5>
											<p class="signer-role">{{ responsable.cargo }}</p>
										</div>
										<div class="signer-status">
											<VaBadge :text="responsable.sign ? 'Firmado' : 'Pendiente'" :color="responsable.sign ? 'success' : 'warning'" class="status-badge" />
											<div v-if="responsable.sign" class="sign-icon">
												<i class="fas fa-check-circle"></i>
											</div>
										</div>
									</div>
									<div v-if="!responsable.sign && key === userCurp" class="signer-actions">
										<div v-if="responsable.signing" class="signing-indicator">
											<i class="fas fa-spinner fa-spin"></i>
											<span>Firmando...</span>
										</div>
										<FirmaComponent
											style="width: 100%; height: 300px"
											:cadenas-a-firmar="cadenasAFirmar"
											:documentos-firmados="props.package.sign"
											:certificado-responsable="certificado.certificadoResponsable"
											@firma-completada="handleFirmaCompletada"
											@firma-error="handleFirmaError" />
										<VaCard class="certificate-card" flat style="margin-top: 15px">
											<VaCardTitle class="card-title">
												<VaIcon name="verified" class="title-icon" />
												Certificado digital del responsable
											</VaCardTitle>
											<VaCardContent>
												<div class="certificate-info">
													<div class="certificate-item">
														<VaIcon name="description" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-name">Nombre del certificado</label>
															<span class="certificate-value">{{ certificado.x509.subject.commonName }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="fingerprint" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-serial">Número de serie</label>
															<span class="certificate-value">{{ certificado.x509.serial }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="event" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-notBefore">Fecha de emisión</label>
															<span class="certificate-value">{{ formatDate(certificado.x509.notBefore) }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="schedule" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-notAfter">Fecha de expiración</label>
															<span class="certificate-value">{{ formatDate(certificado.x509.notAfter) }}</span>
														</div>
													</div>
												</div>
											</VaCardContent>
										</VaCard>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col-md-12 mb-4">
						<div class="width100p mt30 row text-center row" v-if="props.package.sign && !props.package.sent">
							<div class="width70p pl15p pr15p">
								<div class="color-gray mb5" v-if="!props.package.sign && !props.package.error" style="margin-bottom: 10px"><i class="fa fa-refresh fa-spin fa-fw"></i> Esperando validación</div>
								<div class="color-gray mb5" v-if="props.package.error" style="margin-bottom: 10px">
									<i class="fa fa-exclamation-triangle color-crimson" aria-hidden="true"></i>
									{{ props.package.errorMessage }}
								</div>
							</div>
							<VaButton
								v-if="!props.package.error && props.package.sign"
								class="button width100p border bg-blue color-white text-center"
								style="margin-top: 10px"
								@click="getZip(props.package.id, props.package.label)"
								:disabled="!props.package.sign">
								<i class="fa fa-cloud-download pr5"></i> Descargar Zip
							</VaButton>
						</div>
					</div>
				</div>
			</VaCardContent>
			<VaCardActions class="d-flex justify-content-end">
				<VaButton v-if="!props.package.sign" type="button" class="btn btn-primary" @click="enviarFirmas" :disabled="!firmaCompletada">
					<i class="fas fa-paper-plane me-2"></i>
					Enviar firmas
				</VaButton>
			</VaCardActions>
		</VaCard>
	</VaModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 5px;
	padding: 12px 0;
}

.certificate-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	background: #f8f9fa;
	border-radius: 12px;
	border-left: 4px solid #28a745;
}

.certificate-icon {
	color: #28a745;
	font-size: 1.25rem;
}

.certificate-content {
	flex: 1;
}

.certificate-label {
	display: block;
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	margin-bottom: 0.25rem;
}

.certificate-value {
	display: block;
	font-size: 1rem;
	font-weight: 600;
	color: #1a2a3a;
	word-break: break-all;
}
.certificate-card {
	background: white;
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.card-title {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 600;
	color: #1a2a3a;
	padding: 12px 12px 0 12px;
}

/* Estilos para la sección de firmantes */
.signers-section {
	margin-bottom: 2rem;
}

.signers-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.signer-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.signer-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.signer-card.signed {
	border-color: #28a745;
	background: linear-gradient(135deg, #f8fff9 0%, #ffffff 100%);
}

.signer-card.current-user {
	border-color: #007bff;
	background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
	position: relative;
}

.signer-card.current-user::before {
	content: 'Tú';
	position: absolute;
	top: -8px;
	left: 1rem;
	background: #007bff;
	color: white;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
}

.signer-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
}

.signer-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.signer-info {
	flex: 1;
	min-width: 0;
}

.signer-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.signer-role {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

.signer-status {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
}

.status-badge {
	font-weight: 600;
	font-size: 0.8rem;
	padding: 0.25rem 0.75rem;
}

.sign-icon {
	color: #28a745;
	font-size: 1.25rem;
	animation: fadeIn 0.5s ease-in-out;
}

.signer-actions {
	margin-top: 1rem;
	padding-top: 1rem;
	border-top: 1px solid #e9ecef;
}

.signing-indicator {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #007bff;
	font-weight: 500;
	margin-bottom: 1rem;
	padding: 0.75rem;
	background: #f8fbff;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.signing-indicator i {
	font-size: 1rem;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive design */
@media (max-width: 768px) {
	.signer-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.signer-status {
		align-self: flex-end;
	}

	.signer-card {
		padding: 1rem;
	}
}
/* Gradientes para el header */
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
